import { z } from 'zod/v4'

export type ScreenRegionSchema<T extends z.ZodType> = z.ZodDiscriminatedUnion<
  [
    z.ZodObject<{ state: z.ZodLiteral<'ready'>; data: T }, z.core.$strict>,
    z.ZodObject<{ state: z.ZodLiteral<'forbidden'> }, z.core.$strict>,
  ],
  'state'
>

/** A requirement is a non-empty permission key, or the super admin role, and nothing else. */
export const regionRequirementSchema = z.union([
  z.strictObject({ permission: z.string().min(1) }),
  z.strictObject({ role: z.literal('super_admin') }),
])

/**
 * What a region of a screen requires to be shown: a permission key or the super admin role. A region is a part of a
 * screen the caller may not be allowed to see; the screen answers `{ state: 'forbidden' }` for it instead of failing.
 * Inferred from `regionRequirementSchema`, so the type and what `defineScreen` accepts cannot drift.
 */
export type RegionRequirement = Readonly<z.infer<typeof regionRequirementSchema>>

const regions = new WeakSet<z.ZodType>()
/**
 * The `forbidden` variant of every region. A clone of a region (`.describe()`, `.meta()`, `.refine()`) is another
 * instance, so it is not in `regions`, but it shares this variant: finding it where no region is registered is how
 * `defineScreen` knows a region was cloned.
 */
const forbiddenVariants = new WeakSet<z.ZodType>()

/**
 * Wraps the data of a region: `{ state: 'ready', data }` or `{ state: 'forbidden' }` (and nothing else: each variant
 * is strict). `defineScreen` demands that every wrapped field has an entry in `regions` with its requirement, and
 * that every entry of `regions` is wrapped, so a region cannot exist without a permission or the other way round.
 */
export function screenRegion<T extends z.ZodType>(data: T): ScreenRegionSchema<T> {
  const forbidden = z.strictObject({ state: z.literal('forbidden') })
  const schema = z.discriminatedUnion('state', [
    z.strictObject({ state: z.literal('ready'), data }),
    forbidden,
  ])
  regions.add(schema)
  forbiddenVariants.add(forbidden)
  return schema
}

/** True only for the `forbidden` variant `screenRegion` built (shared by every clone of its region). */
export function isForbiddenRegionVariant(schema: z.ZodType): boolean {
  return forbiddenVariants.has(schema)
}

/** True only for the instance `screenRegion` returned. */
export function isScreenRegion(schema: z.ZodType): boolean {
  return regions.has(schema)
}

/** The literal value of a `state` field, or `undefined` when the schema is not an object with a literal `state`. */
function stateLiteralOf(option: z.core.$ZodType): unknown {
  const def = option._zod.def as unknown as {
    type?: string
    shape?: Record<string, z.core.$ZodType>
  }
  if (def.type !== 'object') return undefined
  const state = def.shape?.['state']?._zod.def as unknown as
    | { type?: string; values?: readonly unknown[] }
    | undefined
  return state?.type === 'literal' ? state.values?.[0] : undefined
}

/**
 * True for a union that HAS the shape of a region (an option with `state: 'ready'` and one with `state: 'forbidden'`),
 * whoever built it. `defineScreen` refuses one that `screenRegion()` did not return: a region imitated by hand would
 * pass every other check and still carry no requirement.
 */
export function isRegionShaped(schema: z.ZodType): boolean {
  const def = schema._zod.def as unknown as { type?: string; options?: readonly z.core.$ZodType[] }
  if (def.type !== 'union' || !Array.isArray(def.options)) return false
  const states = def.options.map(stateLiteralOf)
  return states.includes('ready') && states.includes('forbidden')
}
